/**
 * Surviving the OAuth round trip.
 *
 * A redirect destroys in-memory state, so whatever the user was trying to do
 * has to be written down before we leave and picked up when we come back.
 *
 * The nonce is the point. Without it, an abandoned sign-in attempt leaves a
 * stale intent in storage that fires on some unrelated later visit — the user
 * presses nothing and a save happens. The callback URL carries the nonce, and
 * an intent whose nonce does not match is discarded.
 *
 * Pure and storage-agnostic so the rules are testable without a browser.
 */

export type IntentKind = "save-project" | "upload-to-roblox" | "none";

export interface PendingIntent {
  nonce: string;
  kind: IntentKind;
  /** Local project this intent refers to, when it refers to one. */
  projectId?: string;
  /** Where to land afterwards. Same-origin path only. */
  returnTo: string;
  createdAt: number;
}

/** Intents older than this are treated as abandoned. */
export const INTENT_TTL_MS = 10 * 60 * 1000;

export function createIntent(
  kind: IntentKind,
  opts: { projectId?: string; returnTo?: string; nonce: string; now: number },
): PendingIntent {
  return {
    nonce: opts.nonce,
    kind,
    projectId: opts.projectId,
    returnTo: safeReturnTo(opts.returnTo ?? "/"),
    createdAt: opts.now,
  };
}

/**
 * Only same-origin paths are allowed back.
 *
 * `returnTo` ends up in a redirect, so accepting an absolute URL would turn
 * sign-in into an open redirect — a phishing primitive that looks like it came
 * from us. Anything not starting with a single `/` is rejected outright;
 * `//evil.com` is protocol-relative and would leave the origin.
 */
export function safeReturnTo(value: string): string {
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  return value;
}

export interface ResumeCheck {
  ok: boolean;
  intent?: PendingIntent;
  reason?: "missing" | "nonce-mismatch" | "expired" | "malformed";
}

/** Decide whether a stored intent may run for this callback. */
export function resumeIntent(
  stored: PendingIntent | null,
  callbackNonce: string | null,
  now: number,
): ResumeCheck {
  if (!stored) return { ok: false, reason: "missing" };
  if (typeof stored.nonce !== "string" || typeof stored.createdAt !== "number") {
    return { ok: false, reason: "malformed" };
  }
  if (!callbackNonce || callbackNonce !== stored.nonce) {
    return { ok: false, reason: "nonce-mismatch" };
  }
  if (now - stored.createdAt > INTENT_TTL_MS) return { ok: false, reason: "expired" };
  return { ok: true, intent: stored };
}

/** Callback URL handed to the auth provider, carrying the nonce back. */
export function callbackUrlFor(intent: PendingIntent): string {
  const path = safeReturnTo(intent.returnTo);
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}auth=return&n=${encodeURIComponent(intent.nonce)}`;
}

/**
 * A nonce for the sign-in round trip.
 *
 * `crypto.randomUUID` is only defined in a **secure context**. This module runs
 * in the browser as well as the Worker, and a page served over plain http is
 * not one — so on http://robloxclothesmaker.com this threw
 * "crypto.randomUUID is not a function" and sign-in died before it started.
 * `src/app/layers.ts` and `src/app/autosave.ts` already guard the same call and
 * say so in a comment; this was the one that did not.
 *
 * The fallback is `crypto.getRandomValues`, which — unlike `randomUUID` and
 * `crypto.subtle` — *is* available without a secure context, verified in the
 * browser over http. That matters: this value is the CSRF guard on the auth
 * round trip, so degrading to Math.random would turn a crash into a weakness,
 * which is the worse of the two. 16 bytes here, same as a v4 UUID.
 */
export function newNonce(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();

  if (typeof c?.getRandomValues === "function") {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }

  /*
   * No Web Crypto at all. Rather than invent a weak nonce and let the flow
   * continue as though it were protected, refuse — the caller aborts sign-in
   * and the user sees a message, instead of a round trip that looks guarded
   * and is not.
   */
  throw new Error("Web Crypto is unavailable, so sign-in cannot be started securely");
}
